// Live project card (RFC-0009 Task 1.4/1.5) — rendered by Overview and
// Repositories from a `GET /api/projects` list item. Health badges are derived
// from the live fields (PRD-0001 1.2.4 / 1.6): manifest-broken, sync-failed,
// stale, archived, auto-indexed. Links to the project page by repo short name
// (the identifier RFC-0006's `/api/projects/:repo` expects).

import { Link } from 'react-router-dom';
import { GithubLogo, CaretRight } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import type { ProjectListItem } from '@portal/shared-types';

const STATUS_LABEL: Record<ProjectListItem['status'], string> = {
  active: 'Active',
  experimental: 'Experimental',
  maintenance: 'Maintenance',
  archived: 'Archived',
};

export function ProjectCard({ project }: { project: ProjectListItem }) {
  const isArchived = project.status === 'archived' || project.archivedAt !== null;

  return (
    <Link
      to={`/projects/${encodeURIComponent(project.name)}`}
      className="group rounded-xl border border-border bg-surface p-5 shadow-(--c-shadow-sm) transition-shadow hover:shadow-(--c-shadow-md)"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-faint text-muted">
            <GithubLogo size={18} />
          </span>
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-text">{project.name}</h3>
            <p className="truncate font-mono text-[11px] text-muted">{project.fullName}</p>
          </div>
        </div>
        <CaretRight
          size={15}
          weight="bold"
          className="mt-1 shrink-0 text-text/30 transition-colors group-hover:text-text/70"
        />
      </div>

      <p className="mt-3 line-clamp-2 min-h-[2.4em] text-[13px] leading-relaxed text-muted">
        {project.description ?? 'No description provided.'}
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Badge variant="outline">{STATUS_LABEL[project.status]}</Badge>
        {project.manifestBroken && <Badge variant="error">Manifest broken</Badge>}
        {project.lastSyncError !== null && <Badge variant="error">Sync failed</Badge>}
        {project.stale && <Badge variant="warning">Stale</Badge>}
        {isArchived && project.status !== 'archived' && <Badge variant="outline">Archived</Badge>}
        {project.autoIndexed && <Badge variant="ghost">Auto-indexed</Badge>}
      </div>
    </Link>
  );
}
